import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { Lock, Mic } from "lucide-react";

const LOCK_PX = 124;
const CANCEL_PX = 72;
const LOCK_FADE_MS = 640;

type Props = {
  disabled?: boolean;
  ready: boolean;
  locked: boolean;
  onHoldStart: () => void;
  onLocked: () => void;
  onCancel: () => void;
};

/**
 * Micro WhatsApp : tap / relâche / cadenas = même barre posée (corbeille).
 * Glisser vers le cadenas = vert + fade. ← = annuler.
 */
export function ChatVoiceHoldMic({
  disabled = false,
  ready,
  locked,
  onHoldStart,
  onLocked,
  onCancel,
}: Props) {
  const origin = useRef<{ x: number; y: number } | null>(null);
  const micRef = useRef<HTMLButtonElement>(null);
  const lockingRef = useRef(false);
  const [holding, setHolding] = useState(false);
  const [lockHot, setLockHot] = useState(false);
  const [cancelHot, setCancelHot] = useState(false);
  const [locking, setLocking] = useState(false);
  const [railStyle, setRailStyle] = useState<CSSProperties | undefined>();
  const started = useRef(false);

  const placeRail = () => {
    const el = micRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRailStyle({
      position: "fixed",
      left: r.left + r.width / 2,
      bottom: window.innerHeight - r.top + 32,
      transform: "translateX(-50%)",
    });
  };

  useEffect(() => {
    if (!holding) return;
    placeRail();
  }, [holding, ready]);

  const resetHot = () => {
    setLockHot(false);
    setCancelHot(false);
  };

  const endHold = (send: "lock" | "cancel") => {
    origin.current = null;
    started.current = false;
    lockingRef.current = false;
    setHolding(false);
    setLocking(false);
    setRailStyle(undefined);
    resetHot();
    if (send === "cancel") onCancel();
    else onLocked();
  };

  const beginLock = () => {
    if (lockingRef.current) return;
    lockingRef.current = true;
    setLockHot(true);
    setLocking(true);
    try {
      if ("vibrate" in navigator) navigator.vibrate(18);
    } catch {
      /* ignore */
    }
    window.setTimeout(() => endHold("lock"), LOCK_FADE_MS);
  };

  const onDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (disabled || locked || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    origin.current = { x: e.clientX, y: e.clientY };
    started.current = true;
    lockingRef.current = false;
    setHolding(true);
    setLocking(false);
    resetHot();
    placeRail();
    try {
      if ("vibrate" in navigator) navigator.vibrate(12);
    } catch {
      /* ignore */
    }
    onHoldStart();
  };

  const onMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (!origin.current || !started.current || lockingRef.current) return;
    const up = origin.current.y - e.clientY;
    const left = origin.current.x - e.clientX;
    const nextLock = up > LOCK_PX && left < CANCEL_PX * 0.65;
    setLockHot(nextLock);
    setCancelHot(left > CANCEL_PX && up < LOCK_PX);
    if (nextLock) beginLock();
  };

  const onUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (!started.current || lockingRef.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (cancelHot) endHold("cancel");
    else if (lockHot) beginLock();
    else endHold("lock");
  };

  return (
    <div
      className={`chat-voice-hold ${holding ? "is-holding" : ""} ${lockHot ? "is-lock-hot" : ""} ${locking ? "is-locking" : ""} ${cancelHot ? "is-cancel-hot" : ""}`}
    >
      {holding ? (
        <div className="chat-voice-hold__rail" style={railStyle} aria-hidden>
          <span className="chat-voice-hold__lock-pill">
            <span className="chat-voice-hold__lock-burst" />
            <span className="chat-voice-hold__lock-burst chat-voice-hold__lock-burst--late" />
            <Lock className="chat-voice-hold__lock-ico" />
          </span>
          <span className="chat-voice-hold__ticks" />
        </div>
      ) : null}
      <button
        ref={micRef}
        type="button"
        className="chat-voice-hold__mic"
        disabled={disabled}
        aria-label="Appuyer pour enregistrer un vocal"
        title="Appui = poser · ↑ cadenas · ← annuler"
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <span className="chat-voice-hold__ring" aria-hidden />
        <span className="chat-voice-hold__ring chat-voice-hold__ring--late" aria-hidden />
        <Mic className="chat-voice-hold__icon size-5" />
      </button>
    </div>
  );
}
